import React, { useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { SectionHeader } from '../shared/SectionHeader';
import { FormField } from '../shared/FormField';
import { NCGlobalOutputTable } from '../tables/NCGlobalOutputTable';
import { NCFiveYearSummaryTable } from '../tables/NCFiveYearSummaryTable';
import { calculateGPPerUnit, calculateGrossProfit } from '../../bidAnalysisFormulas';
import classes from '../../bidAnalysisTemplate.module.scss';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface NCGlobalOutputSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  /** Live form data for the currently active pack + year (keystrokes visible immediately) */
  formData: any;
  /** yearFormData[packIndex][yearIndex] — persisted per-year data */
  yearFormData: Record<number, Record<number, any>>;
  /** Base pack data (conceptName, packSize, …) keyed by packIndex */
  packSizeFormData: Record<number, any>;
  activePackSizeTab: number | null;
  activeYearTab: number | null;
  allPackSizeTabs: Array<{
    index: number;
    conceptName?: string;
    packSize?: string;
    label?: string;
  }>;
  /** Approved By from bid-details (bid-level). Table shows "—" when null. */
  bidApprovedBy?: string | null;
  /** Global Project Information field — same for all pack sizes/years in this bid. */
  gsmsHndlgType?: string;
  onFieldChange?: (field: string, value: any) => void;
  onFieldBlur?: (field: string, event?: any) => void;
  onFieldKeyDown?: (field: string, e: React.KeyboardEvent) => void;
  onSetActivePackSizeTab?: (tabIndex: number) => void;
  onSetActiveYearTab?: (yearIndex: number) => void;
  copyForAllYears?: () => void;
}

// ---------------------------------------------------------------------------
// Number of year indices (0 = Base Year, 1-4 = Option Years)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const NCGlobalOutputSection: React.FC<NCGlobalOutputSectionProps> = ({
  isExpanded,
  onToggle,
  formData,
  yearFormData,
  packSizeFormData,
  activePackSizeTab,
  activeYearTab,
  allPackSizeTabs,
  bidApprovedBy,
  gsmsHndlgType,
  onFieldChange,
  onFieldBlur,
  onFieldKeyDown,
  onSetActivePackSizeTab,
  onSetActiveYearTab,
  copyForAllYears,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);

  /**
   * Aggregate totals across ALL pack sizes for the ACTIVE year only.
   * This gives the current-year Gross Profit % and GP/Unit shown below the table.
   */
  const calculatedValues = useMemo(() => {
    const yearIndex = activeYearTab ?? 0;
    let totalRevenue = 0;
    let totalGP = 0;
    let totalUsage = 0;

    allPackSizeTabs.forEach(packTab => {
      const baseData = packSizeFormData[packTab.index] || {};
      const yearData = yearFormData[packTab.index]?.[yearIndex] || {};
      const isActive = activePackSizeTab === packTab.index && activeYearTab === yearIndex;
      const packData = isActive
        ? { ...baseData, ...yearData, ...formData }
        : { ...baseData, ...yearData };

      const usage = parseFloat(packData.ncEstAnnualUsage || packData.targetAnnualUsage || '0') || 0;
      const bidPrice =
        parseFloat(packData.suggestedBidPrice || packData.suggested_bid_price || '0') || 0;
      const gsmsProfitRaw = packData.gsmsProfit ?? packData.gsms_profit;
      const hasExplicitProfit =
        gsmsProfitRaw !== undefined && gsmsProfitRaw !== null && gsmsProfitRaw !== '';
      const profit = hasExplicitProfit ? parseFloat(String(gsmsProfitRaw)) || 0 : null;

      let revenue: number;
      let gp: number;

      if (usage > 0 && bidPrice > 0) {
        revenue = usage * bidPrice;
        if (profit !== null) {
          gp = profit !== 0 ? revenue * (profit / 100) : 0;
        } else {
          gp = parseFloat(packData.contractAnnualGP || '0') || 0;
        }
      } else {
        revenue = parseFloat(packData.contractAnnualRevenue || '0') || 0;
        gp = parseFloat(packData.contractAnnualGP || '0') || 0;
      }

      if (!isNaN(revenue)) totalRevenue += revenue;
      if (!isNaN(gp)) totalGP += gp;
      if (!isNaN(usage)) totalUsage += usage;
    });

    const grossProfit = calculateGrossProfit(totalRevenue, totalGP);
    const gpPerUnit = calculateGPPerUnit(totalGP, totalUsage);

    return { grossProfit, gpPerUnit };
  }, [formData, yearFormData, packSizeFormData, allPackSizeTabs, activePackSizeTab, activeYearTab]);

  const formatNumber = (value: number, decimals = 2): string => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.' + '0'.repeat(decimals);
    }
    return value.toFixed(decimals);
  };

  const formatCurrency = (value: number): string => {
    if (value === null || value === undefined || isNaN(value)) return '$0.00';
    return `$${value.toFixed(2)}`;
  };

  return (
    <div className={classes.section}>
      <SectionHeader title="Global Output Section" isExpanded={isExpanded} onToggle={onToggle}>
        {/* Show Edit / Done button only when an edit handler is provided */}
        {!!onFieldChange && (
          <Button
            label={isEditMode ? 'Done' : 'Edit'}
            icon={isEditMode ? 'pi pi-check' : 'pi pi-pencil'}
            size="small"
            severity={isEditMode ? 'success' : 'secondary'}
            onClick={e => {
              e.stopPropagation();
              setIsEditMode(prev => !prev);
            }}
            style={{
              marginRight: '0.5rem',
              ...(isEditMode
                ? {}
                : { backgroundColor: '#6B21A8', borderColor: '#6B21A8', color: '#fff' }),
            }}
          />
        )}
      </SectionHeader>

      {isExpanded && (
        <div className={classes.sectionContent}>
          {/* Per-year detail table — all pack sizes × all years in a single scrollable grid */}
          <NCGlobalOutputTable
            yearFormData={yearFormData}
            packSizeFormData={packSizeFormData}
            formData={formData}
            activePackSizeTab={activePackSizeTab}
            activeYearTab={activeYearTab}
            allPackSizeTabs={allPackSizeTabs}
            bidApprovedBy={bidApprovedBy}
            gsmsHndlgType={gsmsHndlgType}
            isEditMode={isEditMode}
            onFieldChange={onFieldChange}
            onFieldBlur={onFieldBlur}
            onFieldKeyDown={onFieldKeyDown}
            onSetActivePackSizeTab={onSetActivePackSizeTab}
            onSetActiveYearTab={onSetActiveYearTab}
            copyForAllYears={copyForAllYears}
          />

          {/* 5-Year Summary — key metrics rolled up per year and across all 5 years */}
          <div style={{ marginTop: '2.5rem' }}>
            <h3
              style={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: '#4d2e6d',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: '0.75rem',
              }}
            >
              5-Year Summary
            </h3>
            <NCFiveYearSummaryTable
              yearFormData={yearFormData}
              packSizeFormData={packSizeFormData}
              formData={formData}
              activePackSizeTab={activePackSizeTab}
              activeYearTab={activeYearTab}
              allPackSizeTabs={allPackSizeTabs}
            />
          </div>

          {/* Calculated summary — aggregated across all packs and all years */}
          {(() => {
            const yearLabel =
              activeYearTab === null || activeYearTab === 0 ? 'Base Year' : `OY${activeYearTab}`;
            return (
              <div
                className={classes.formGrid2}
                style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}
              >
                <FormField label={`Gross Profit (${yearLabel})`}>
                  <InputText
                    value={`${formatNumber(calculatedValues.grossProfit, 2)}%`}
                    disabled
                    readOnly
                  />
                </FormField>

                <FormField label={`GP / Unit (${yearLabel})`}>
                  <InputText value={formatCurrency(calculatedValues.gpPerUnit)} disabled readOnly />
                </FormField>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};
